import { useEffect, useState } from 'react';
import { searchExerciseIds } from '../lib/database';
import { normalizeExerciseName, MIN_QUERY_LENGTH } from '../lib/exerciseSearch';

/**
 * Id dei template che matchano la query per ALIAS, dalla RPC alias-aware.
 *
 * Serve a Builder ed esecuzione, che filtrano una lista gia' in memoria: il
 * match locale resta istantaneo e gli alias arrivano subito dopo, senza mai
 * bloccare la digitazione. Se la rete non risponde `searchExerciseIds` torna
 * un array vuoto e la ricerca degrada al filtro locale — importante durante un
 * allenamento, dove la connessione in palestra e' quello che e'.
 */

const EMPTY_IDS = new Set();
const DEBOUNCE_MS = 220;

// Cache di modulo: dentro una sessione si cercano spesso gli stessi esercizi,
// e riaprire il sheet non deve ripagare la stessa RPC.
const CACHE_LIMIT = 60;
const cache = new Map();

export const useExerciseAliasIds = (query) => {
  const normalizedQuery = normalizeExerciseName(query);
  const [ids, setIds] = useState(EMPTY_IDS);

  useEffect(() => {
    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setIds(EMPTY_IDS);
      return undefined;
    }

    const cached = cache.get(normalizedQuery);
    if (cached) {
      setIds(cached);
      return undefined;
    }

    // Azzera subito: gli id della query precedente non valgono per questa, e
    // mostrarli produrrebbe risultati che non c'entrano nulla con cio' che si
    // sta digitando.
    setIds(EMPTY_IDS);

    let cancelled = false;
    const timer = setTimeout(async () => {
      const found = await searchExerciseIds(normalizedQuery);
      if (cancelled) return;

      const result = new Set(found);
      if (cache.size >= CACHE_LIMIT) cache.clear();
      cache.set(normalizedQuery, result);
      setIds(result);
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [normalizedQuery]);

  return ids;
};

export default useExerciseAliasIds;
